/**
 * DealTable.js — the deal list, as a plain table
 * ==============================================
 * Usability review of 23 October 2026, item 21: *"Replace it with a plain
 * single-line table (Deal | Strategy | Status | Invested | Cash back | P&L |
 * MOIC | IRR | Ccy) that uses the full page height and is easy to scan and
 * sort. Current problems: it scrolls inside a small box within the page; each
 * deal takes two lines, with the name above and the numbers below; the column
 * headings don't line up with the numbers; the currency sits in the IRR
 * column."*
 *
 * So: a real table, one row per deal, every heading a sort, the header held at
 * the top of the window while the page scrolls, and no box of its own to
 * scroll in. The name is the deal's, with the company and the ticker under it
 * in small type (item 7) — the company read off the security's issuer before
 * the counterparty, because a counterparty can be a ticker (CLAUDE.md 0.2).
 * The figures are the ledger's (`stats`), compact, each with its exact figure
 * on hover and in the CSV; a row opens the deal's own page.
 *
 * The honest-labelling rules stay: n/a is a figure the code could not stand
 * behind, never 0.00 (0.30); a break is marked and its P&L, MOIC and IRR are
 * floors that sort last; a deal whose cost basis is incomplete is marked
 * provisional.
 */
import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { InfoTip } from "../../components/PageTools";
import { dealReportPath } from "../../components/links";
import theme from "../../components/theme";
import { dealSubline } from "./figures";
import { strategyLabel } from "./listing";
import { STAT_COLUMNS, statExact, statText, statTitle, statTone, unrankedCount } from "./stats";

export const LIST_COLUMNS = [
  { key: "name", label: "Deal", className: "dr-cell-deal", title: "The deal, with the company and its ticker underneath." },
  { key: "strategy", label: "Strategy", className: "dr-cell-strategy", title: "The kind of deal, as tagged on the Deals page." },
  { key: "status", label: "Status", className: "dr-cell-status", title: "Active or Closed, as recorded on the deal." },
  ...STAT_COLUMNS.map((c) => ({ ...c, numeric: true, className: "num" })),
  {
    key: "currency",
    label: "Ccy",
    className: "dr-cell-ccy",
    title: "The currency the row's figures are in: the deal's own, or dollars converted at each date's fixing where its cash spans currencies.",
  },
];

function SortHeading({ column, sort, onSort }) {
  const on = sort && sort.key === column.key;
  const ariaSort = on ? (sort.dir === "asc" ? "ascending" : "descending") : "none";
  const firstText = column.numeric ? "highest first" : "A to Z";
  const nextText = !on ? firstText : sort.dir === (column.numeric ? "desc" : "asc") ? (column.numeric ? "lowest first" : "Z to A") : "back to the page's own order";
  return (
    <th className={column.className} aria-sort={ariaSort} scope="col">
      <button
        type="button"
        className={`dr-sort${on ? " is-on" : ""}`}
        onClick={() => onSort(column.key)}
        // What the column is, and what the click will do with it. A heading
        // that sorts and does not say so is read as a label.
        title={`${column.title}\n\nClick to sort: ${nextText}.`}
      >
        {column.label}
        <span aria-hidden="true" className="dr-sort-mark">
          {on ? (sort.dir === "desc" ? "↓" : "↑") : "↕"}
        </span>
      </button>
    </th>
  );
}

export function DealRow({ deal, onOpen, highlighted }) {
  const ref = useRef(null);
  const stats = deal.stats;
  useEffect(() => {
    // The deal you came back from, in view and marked, so the way back lands
    // where you left.
    if (highlighted && ref.current && typeof ref.current.scrollIntoView === "function") {
      ref.current.scrollIntoView({ block: "center" });
    }
  }, [highlighted]);
  const sub = dealSubline(deal.deal);
  const provisional = deal.summary && deal.summary.basis_complete === false;
  return (
    <tr
      ref={ref}
      className={`dr-row-link${highlighted ? " is-last" : ""}`}
      onClick={(e) => {
        // The name is a real link; a click on it navigates once, not twice.
        if (e.target.closest && e.target.closest("a")) return;
        onOpen(deal.deal.id);
      }}
    >
      <td className="dr-cell-deal">
        <Link to={dealReportPath(deal.deal.id)} className="dr-deal-name">
          {deal.deal.name}
        </Link>
        {stats && stats.carrying_is_negative && (
          <span
            className="dr-chip dr-chip-break"
            title="The investment accounts net below zero, so nothing can be counted as held: P&L, MOIC and IRR count the cash only and are floors."
          >
            break
          </span>
        )}
        {provisional && (
          <span className="dr-chip dr-chip-provisional" title="Some disposals have no cost loaded, so P&L and MOIC overstate.">
            provisional
          </span>
        )}
        {sub && (
          <div className="dr-deal-sub" title={sub}>
            {sub}
          </div>
        )}
      </td>
      <td className="dr-cell-text dr-cell-strategy" style={deal.deal.strategy ? undefined : { color: theme.textMuted, fontStyle: "italic" }}>
        {strategyLabel(deal.deal.strategy)}
      </td>
      <td className="dr-cell-text dr-cell-status">{(deal.deal.status || "").trim() || "—"}</td>
      {STAT_COLUMNS.map((c) => {
        const exact = statExact(stats, c);
        return (
          <td key={c.key} className="num" style={{ color: statTone(stats, c) }} title={[exact, statTitle(stats, c)].filter(Boolean).join("\n\n")}>
            {statText(stats, c)}
          </td>
        );
      })}
      <td className="dr-cell-ccy" title={stats && stats.reads_as ? stats.reads_as : undefined}>
        {stats && stats.currency ? stats.currency : ""}
        {stats && stats.converted ? <span className="dr-conv"> conv</span> : null}
      </td>
    </tr>
  );
}

export function DealTable({ deals, sort, onSort, onOpen, lastDealId }) {
  return (
    <div className="dr-list-scroll">
      <table className="dr-table dr-list" aria-label="Deal list">
        <thead>
          <tr>
            {LIST_COLUMNS.map((c) => (
              <SortHeading key={c.key} column={c} sort={sort} onSort={onSort} />
            ))}
          </tr>
        </thead>
        <tbody>
          {deals.map((deal) => (
            <DealRow key={deal.deal.id} deal={deal} onOpen={onOpen} highlighted={deal.deal.id === lastDealId} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* What the figures are and what they cannot say, under the table. Short on
   purpose: it carries only the counts a reader cannot get from a heading, and
   the method sits behind the ⓘ. */
export function DealTableFooter({ deals, sort }) {
  const withheld = deals.filter((d) => d.stats && d.stats.measured === false);
  const negative = deals.filter((d) => d.stats && d.stats.carrying_is_negative);
  const provisional = deals.filter((d) => d.summary && d.summary.basis_complete === false);
  const unranked = unrankedCount(deals, sort);
  const sorted = sort ? LIST_COLUMNS.find((c) => c.key === sort.key) : null;
  const parts = [];
  if (withheld.length) {
    parts.push(
      `${withheld.length} ${withheld.length === 1 ? "reads" : "read"} n/a: ${withheld.length === 1 ? "its" : "their"} cash spans currencies and an exchange rate is missing for one of the dates, so there is no single figure.`
    );
  }
  if (negative.length) {
    parts.push(
      `${negative.length} ${negative.length === 1 ? "is" : "are"} marked break: the investment accounts net below zero, so nothing can be held and the figures count the cash only.`
    );
  }
  if (provisional.length) {
    parts.push(
      `${provisional.length} ${provisional.length === 1 ? "is" : "are"} provisional: some disposals have no cost loaded, so P&L and MOIC overstate.`
    );
  }
  if (sorted) {
    const dir = sorted.numeric ? (sort.dir === "desc" ? "highest first" : "lowest first") : sort.dir === "asc" ? "A to Z" : "Z to A";
    parts.push(
      `Sorted by ${sorted.label}, ${dir}${
        unranked ? `; ${unranked} ${unranked === 1 ? "row has" : "rows have"} no comparable ${sorted.label} and ${unranked === 1 ? "sits" : "sit"} at the bottom` : ""
      }.`
    );
  } else {
    parts.push("In the page's own order: the most cash tied up at once first.");
  }
  return (
    <div className="dr-list-footer">
      <span>
        From the general ledger, each deal in its own currency.{" "}
        <InfoTip wide>
          <p>
            Invested is capital: notes, preferred, warrant exercises, PPLNs, shares subscribed, and
            the counsel paid at their closing. An equity line puts none in, so its draws come off cash
            back instead, and so do market buys around a position, the part of an ACE deal's units
            its same-day sale paid for, later legal and any other cash out; P&amp;L is the same either way.
          </p>
          <p>
            P&amp;L and MOIC count what is still held as if realised today, and most of this book is
            carried at cost — so a deal with nothing back reads nil and 1.00x, which is “nothing
            recognised yet”.
          </p>
          <p>
            A deal whose cash spans currencies reads in dollars, each flow converted at its own date's
            Riksbank fixing (marked conv); every other deal reads in its own currency, which is why a
            krona deal needs no exchange rate to have a multiple.
          </p>
          <p>Hover a figure for the exact amount; the CSV carries every figure unrounded.</p>
        </InfoTip>{" "}
        {parts.join(" ")}
      </span>
    </div>
  );
}

export const DEAL_CSV_COLUMNS = [
  { label: "Deal", value: (d) => d.deal.name },
  { label: "Company", value: (d) => d.deal.issuer || d.deal.counterparty || "" },
  { label: "Ticker", value: (d) => d.deal.ticker || "" },
  { label: "Strategy", value: (d) => strategyLabel(d.deal.strategy) },
  { label: "Status", value: (d) => (d.deal.status || "").trim() },
  { label: "Invested", value: (d) => (d.stats && d.stats.measured !== false ? d.stats.capital_deployed : null) },
  { label: "Cash back", value: (d) => (d.stats && d.stats.measured !== false ? d.stats.cash_returned_net : null) },
  { label: "P&L", value: (d) => (d.stats && d.stats.measured !== false ? d.stats.net_profit : null) },
  { label: "MOIC", value: (d) => (d.stats && d.stats.measured !== false ? d.stats.moic : null) },
  { label: "IRR %", value: (d) => (d.stats && d.stats.measured !== false ? d.stats.irr_pct : null) },
  { label: "Paid out, not capital", value: (d) => (d.stats && d.stats.measured !== false ? d.stats.paid_out_not_capital : null) },
  { label: "Cash back, gross", value: (d) => (d.stats && d.stats.measured !== false ? d.stats.cash_returned : null) },
  { label: "Every outflow", value: (d) => (d.stats && d.stats.measured !== false ? d.stats.gross_deployed : null) },
  { label: "Currency", value: (d) => (d.stats && d.stats.currency) || "" },
  { label: "Converted", value: (d) => (d.stats && d.stats.converted ? "yes" : "") },
  {
    label: "Note",
    value: (d) =>
      !d.stats
        ? "no figures"
        : d.stats.measured === false
        ? `n/a: ${d.stats.withheld_because || "withheld"}`
        : d.stats.carrying_is_negative
        ? "break: P&L, MOIC and IRR are floors"
        : d.summary && d.summary.basis_complete === false
        ? "provisional: cost basis incomplete"
        : "",
  },
];

